import * as Sentry from '@sentry/browser';
import { format } from 'date-fns';
import posthog from 'posthog-js';
import { Controller, type SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { SdkError } from 'wg-api-sdk';

import { DatePicker } from '@/components/common/DatePicker';
import SubmitButton from '@/components/common/SubmitButton';
import type { ModalProps } from '@/components/providers/ModalProvider/ModalProvider';
import { Checkbox } from '@/components/shadcn/checkbox';
import { Field, FieldLabel } from '@/components/shadcn/field';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/shadcn/select';
import { Textarea } from '@/components/shadcn/textarea';
import useEmailClient from '@/features/email/hooks/useEmailClient';
import useCreateNote from '@/features/notes/mutations/useCreateNote';
import type { Party } from '@/features/party/types';

import useActionsBrio from '../../hooks/useActionsBrio';
import useBrioEmployees from '../../queries/hooks/useBrioEmployees';
import type {
  BrioTaskPriority,
  BrioTaskType,
  CreateTaskVariablesBrio,
} from '../../types';
import AssigneeField from '../AssigneeField';
import PriorityField from '../PriorityField';

type CreateTaskValues = {
  type: BrioTaskType;
  description: string;
  priority: BrioTaskPriority;
  date: Date;
  assignee?: { label: string; value: string };
  isPersonalNote: boolean;
  isVisibleInMyBroker: boolean;
};

type Props = ModalProps & {
  defaultValues?: Partial<Omit<CreateTaskVariablesBrio, 'priority'>>;
  party: Maybe<Party>;
};

const CRM_NAME = 'BRIO';
const CRM_KEY = 'brio';

const TASK_TYPES: Array<BrioTaskType> = [
  'APPOINTMENT',
  'TASK',
  'EVENT',
  'ARCHIVE_CORRESPONDENCE',
  'AUTOMATIC_ACTIVITY',
  'COMMUNICATION',
  'OFFICE_VISIT',
  'E_MARKETING',
  'CONFLICT_OF_INTEREST',
  'EXPERTISE',
  'INSPECTION',
  'INTERNAL_DISCUSSION',
  'COMPLAINT',
  'COMMERCIAL_ACTION',
  'ANTI_MONEY_LAUNDERING',
  'REFUSAL_SUBSCRIPTION',
];

export default function CreateTaskModalBrio({
  onClose,
  defaultValues,
  party,
}: Props) {
  const { t } = useTranslation();

  const { data: employees } = useBrioEmployees();

  const {
    register,
    handleSubmit,
    control,
    formState: { isSubmitting },
  } = useForm<CreateTaskValues>({
    defaultValues: {
      type: 'TASK',
      description: defaultValues?.description,
      priority: 'NORMAL',
      date: defaultValues?.dueDate
        ? new Date(defaultValues.dueDate)
        : new Date(),
      isPersonalNote: false,
      isVisibleInMyBroker: false,
    },
  });

  const { createTask } = useActionsBrio();
  const { mutateAsync: createNote } = useCreateNote();
  const { getSelectedMessageId } = useEmailClient();

  const crmPartyId = party?.external_refs.find(
    (ref) => ref.company.toLowerCase() === CRM_KEY,
  )?.ref;

  const onSubmit: SubmitHandler<CreateTaskValues> = async (data) => {
    if (!crmPartyId) return;

    try {
      await createTask({
        description: data.description,
        type: data.type,
        priority: data.priority,
        dueDate: format(data.date, 'yyyy-MM-dd'),
        assignedTo: data.assignee?.value,
        isPersonalNote: data.isPersonalNote,
        isVisibleInMyBroker: data.isVisibleInMyBroker,
        crmPartyId,
      });

      posthog.capture('Task created', {
        crmBackend: CRM_KEY,
        partyId: party.id,
        messageId: getSelectedMessageId(),
        priority: data.priority,
        type: data.type,
        hasAssignee: Boolean(data.assignee?.value),
      });
      toast.success(t('TASK_CREATED', { crm: CRM_NAME }));

      const typeLabel = t(`BRIO_TASK_TYPE_${data.type}`);
      const noteHtml = `<p><strong>${t('TASK_CREATED_NOTE_PREFIX', {
        crm: CRM_NAME,
      })}:</strong> ${typeLabel}</p><p>${data.description}</p>`;

      await createNote({
        partyId: party.id,
        title: typeLabel,
        text: noteHtml,
      });

      onClose();
    } catch (error) {
      toast.error(t('TASK_CREATE_FAILED'), {
        description: error instanceof SdkError ? error.message : undefined,
      });
      Sentry.captureException(error);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4 p-2">
      <div className="flex flex-col gap-2">
        <Controller
          control={control}
          name="type"
          render={({ field }) => (
            <Field>
              <FieldLabel>{t('TASK_FIELD_TYPE')}</FieldLabel>
              <Select value={field.value} onValueChange={field.onChange}>
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TASK_TYPES.map((type) => (
                    <SelectItem key={type} value={type}>
                      {t(`BRIO_TASK_TYPE_${type}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          )}
        />

        <Field>
          <FieldLabel htmlFor="description">
            {t('TASK_FIELD_DESCRIPTION')}
          </FieldLabel>
          <Textarea
            id="description"
            {...register('description', { required: true })}
            placeholder={t('TASK_FIELD_DESCRIPTION_PH')}
            className="h-48"
          />
        </Field>

        <PriorityField<CreateTaskValues>
          control={control}
          priorities={[
            { label: t('PRIORITY_BRIO_LOW'), value: 'LOW' },
            { label: t('PRIORITY_BRIO_NORMAL'), value: 'NORMAL' },
            { label: t('PRIORITY_BRIO_HIGH'), value: 'HIGH' },
          ]}
        />

        <Controller
          control={control}
          name="date"
          render={({ field }) => (
            <Field>
              <FieldLabel>{t('TASK_FIELD_END_DATE_BRIO')}</FieldLabel>
              <DatePicker {...field} />
            </Field>
          )}
        />

        <AssigneeField control={control} employees={employees} />

        <div className="mt-2 flex flex-col gap-2">
          <Controller
            control={control}
            name="isPersonalNote"
            render={({ field }) => (
              <Field orientation="horizontal">
                <Checkbox
                  id="isPersonalNote"
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
                <FieldLabel htmlFor="isPersonalNote" className="cursor-pointer">
                  {t('TASK_FIELD_IS_PERSONAL_NOTE')}
                </FieldLabel>
              </Field>
            )}
          />

          <Controller
            control={control}
            name="isVisibleInMyBroker"
            render={({ field }) => (
              <Field orientation="horizontal">
                <Checkbox
                  id="isVisibleInMyBroker"
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
                <FieldLabel
                  htmlFor="isVisibleInMyBroker"
                  className="cursor-pointer"
                >
                  {t('TASK_FIELD_IS_VISIBLE_IN_MY_BROKER')}
                </FieldLabel>
              </Field>
            )}
          />
        </div>
      </div>

      <div className="flex justify-end">
        <SubmitButton isSubmitting={isSubmitting}>{t('CREATE')}</SubmitButton>
      </div>
    </form>
  );
}
